import { RefObject, useCallback, useRef, useState } from "react";

import { Stack, IconButton, Box, Tooltip } from "@mui/material";
import {
  XCloseIcon,
  ZoomInIcon,
  ZoomOutIcon,
  Typography,
  Download01Icon,
  Minimize01Icon,
  Maximize01Icon,
} from "components";
import { colors } from "configs";
import { initLanContent } from "helpers";
// eslint-disable-next-line import/no-extraneous-dependencies
import html2canvas from "html2canvas";
// eslint-disable-next-line import/no-extraneous-dependencies
import jsPDF from "jspdf";
import { Tree, TreeNode, TreeProps } from "react-organizational-chart";
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import { rem } from "utils";

import TreeNodeLabel from "./Node";
import TreeNodeUserLabel from "./NodeUser";
import { OrgChartNode, ActionProps } from "./OrgTree.types";

interface OrgTreeProps {
  title: string;
  treeProps?: Partial<TreeProps>;
  orgChart: OrgChartNode[];
  orgName: string;
  actions?: ActionProps;
  transformRef?: RefObject<ReactZoomPanPinchRef>;
}

// Helper to get all node ids recursively
function getAllNodeIds(nodes: OrgChartNode[]): (string | number)[] {
  let ids: (string | number)[] = [];
  for (const node of nodes) {
    ids.push(node.id);
    if (node.children && node.children.length > 0) {
      ids = ids.concat(getAllNodeIds(node.children));
    }
  }
  return ids;
}

const OrgTree = (props: OrgTreeProps) => {
  const {
    title,
    treeProps,
    orgChart = [] as OrgChartNode[],
    orgName = "",
    actions = {} as ActionProps,
    transformRef,
  } = props;

  const orgChartRef = useRef();
  const [collapsedNodes, setCollapsedNodes] = useState<
    Record<string | number, boolean>
  >({});
  const [rootCollapsed, setRootCollapsed] = useState(false);
  const handleToggleCollapse = useCallback((nodeId: string | number) => {
    setCollapsedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  }, []);
  const [showEmployees, setShowEmployees] = useState<
    Record<string | number, boolean>
  >({});

  const handleExportPdf = async () => {
    const element = orgChartRef.current;
    // Chụp DOM thành ảnh
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
    const canvas = await html2canvas(element as any, {
      useCORS: true, // nếu org chart có load ảnh từ ngoài
      scale: 2, // tăng chất lượng ảnh nếu cần
    });
    const imgData = canvas.toDataURL("image/png");

    // Tạo PDF
    // eslint-disable-next-line new-cap
    const pdf = new jsPDF({
      orientation: "landscape", // hoặc "portrait" tùy org chart
      unit: "pt",
      format: "a4",
    });

    // Tính toán kích thước phù hợp
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save("DoHR_Org_Chart.pdf");
  };

  const renderOrgChart = useCallback(
    (nodes: OrgChartNode[]): JSX.Element[] => {
      return nodes.map((node) => {
        const isCollapsed = collapsedNodes[node.id];
        const isShowEmployees = showEmployees[node.id];
        return (
          <TreeNode
            key={node.id}
            label={
              <TreeNodeLabel
                id={node.id}
                title={node.name}
                extendInfo={{
                  nodeData: node,
                  totalEmployee: node?.employees?.length ?? 0,
                }}
                actions={actions}
                isCollapsed={isCollapsed}
                hasChildren={!!node.children?.length}
                onToggleCollapse={() => handleToggleCollapse(node.id)}
                isShowEmployees={isShowEmployees}
                onToggleShowEmployees={() =>
                  setShowEmployees((prev) => ({
                    ...prev,
                    [node.id]: !prev[node.id],
                  }))
                }
              />
            }
          >
            {!isCollapsed && (
              <>
                {!!node.children?.length && renderOrgChart(node.children)}
                {isShowEmployees &&
                  node.employees?.map((employee) => (
                    <TreeNode
                      key={employee.id}
                      label={
                        <TreeNodeUserLabel
                          key={employee.id}
                          id={employee.id}
                          title={employee.title}
                          fullName={employee.fullName}
                          email={employee.email}
                        />
                      }
                    />
                  ))}
              </>
            )}
          </TreeNode>
        );
      });
    },
    [actions, collapsedNodes, handleToggleCollapse, showEmployees]
  );

  return (
    <TransformWrapper
      doubleClick={{ disabled: true }}
      centerOnInit
      wheel={{
        excluded: ["wheelDisabled"],
      }}
      initialScale={0.7}
      minScale={0.7}
      ref={transformRef}
    >
      {({ zoomIn, zoomOut, resetTransform }) => (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              fontWeight={600}
              color="grey.900"
              type="DisplaySM"
              variant="h1"
            >
              {title}
            </Typography>
            <Stack
              direction="row"
              columnGap={rem(8)}
              position="absolute"
              top="-12px"
              right="-12px"
              zIndex={3}
              boxShadow="rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px;"
            >
              <IconButton aria-label="zoom-in" onClick={() => zoomIn()}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
              <IconButton aria-label="zoom-out" onClick={() => zoomOut()}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
              {/* Expand All */}
              <Tooltip title={initLanContent(["Mở rộng tất cả", "Expand All"])}>
                <IconButton
                  onClick={() => {
                    setRootCollapsed(false);
                    const allIds = getAllNodeIds(orgChart);
                    setCollapsedNodes((prev) => {
                      const newState = { ...prev };
                      allIds.forEach((id) => {
                        newState[id] = false;
                      });
                      return newState;
                    });
                  }}
                >
                  <Maximize01Icon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* Collapse All */}
              <Tooltip title={initLanContent(["Đóng tất cả", "Collapse All"])}>
                <IconButton
                  aria-label="collapse-all"
                  onClick={() => {
                    setRootCollapsed(true);
                    const allIds = getAllNodeIds(orgChart);
                    setCollapsedNodes((prev) => {
                      const newState = { ...prev };
                      allIds.forEach((id) => {
                        newState[id] = true;
                      });
                      return newState;
                    });
                    setShowEmployees((prev) => {
                      const newState = { ...prev };
                      const allNodeIds = getAllNodeIds(orgChart);
                      allNodeIds.forEach((id) => {
                        newState[id] = false;
                      });
                      return newState;
                    });
                  }}
                >
                  <Minimize01Icon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={initLanContent(["Tải xuống", "Download"])}>
                <IconButton
                  onClick={() => {
                    handleExportPdf();
                  }}
                >
                  <Download01Icon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={initLanContent(["Đặt lại zoom", "Reset Zoom"])}>
                <IconButton aria-label="clear" onClick={() => resetTransform()}>
                  <XCloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Box
            height="calc(100vh - 120px)"
            sx={{
              "& ul:empty": {
                display: "none",
              },
            }}
            ref={orgChartRef}
          >
            <TransformComponent
              wrapperStyle={{
                marginTop: "12px",
                width: "100%",
                height: "100%",
                backgroundColor: colors.gray[50],
                borderRadius: "12px",
              }}
            >
              <Tree
                lineWidth="2px"
                lineColor="#A9A9A9"
                lineBorderRadius="10px"
                label={
                  <TreeNodeLabel
                    id={0}
                    isRootNode
                    extendInfo={{
                      nodeData: {
                        id: 0,
                        name: orgName,
                        children: [],
                        employees: [],
                      },
                    }}
                    isCollapsed={rootCollapsed}
                    onToggleCollapse={() => setRootCollapsed((prev) => !prev)}
                    title={orgName}
                    actions={actions}
                  />
                }
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...treeProps}
              >
                {!rootCollapsed && renderOrgChart(orgChart)}
              </Tree>
            </TransformComponent>
          </Box>
        </>
      )}
    </TransformWrapper>
  );
};

export default OrgTree;
